import { wsBaseUrl } from "@constants";
import React from "react";
import {
  normalizeNewCitationPacket,
  mergeCitationItems,
  CitationViewItem,
  formatDocLabel,
  formatTabularLabel,
} from "./citations";
import { renderMessagesForDisplay } from "./renderMessages";
import { InlineBadge } from "./messageParts";
import { Option } from "../components/AssistantForm";
import { AssistantSelectedFiles } from "@services/ProjectChatContext";
import { NavigateOptions, URLSearchParamsInit } from "react-router-dom";
import { LoadingEventType } from "./useLoadingStream";
import { LoadingStepData } from "./useAssistantLoaderState";

export interface ChatLog {
  message: string | React.ReactNode | React.ReactNode[];
  sender: "user" | "bot";
  citations?: any[];
  images?: any;
  generated_images_s3_keys?: any;
  attached_files?: { id: string; name: string; status?: string }[];
}

export const useAssistantSocket = (
  citationsV2Ref: React.RefObject<CitationViewItem[]>,
  inlineBadgesRef: React.RefObject<InlineBadge[]>,
  nextBadgeNumRef: React.RefObject<number>,
  selectedProject: Option,
  setError: (value: React.SetStateAction<string | null>) => void,
  selectedFiles: AssistantSelectedFiles[],
  enabledSources: Set<string>,
  chatSessionId: string | null,
  conversationIdRef: React.RefObject<string | null>,
  selectedReport: Option,
  companyName: string,
  setChatSessionId: (id: string | null) => void,
  chatSessionIdRef: React.RefObject<string | null>,
  setConversationId: (value: React.SetStateAction<string>) => void,
  conversationIdParam: string | undefined,
  setSearchParams: (
    nextInit?:
      | URLSearchParamsInit
      | ((prev: URLSearchParams) => URLSearchParamsInit),
    navigateOpts?: NavigateOptions
  ) => void,
  setIsChatLoading: (value: React.SetStateAction<boolean>) => void,
  setShowLoading: (value: React.SetStateAction<boolean>) => void,
  thinkingReceived: React.RefObject<boolean>,
  ingest: (type: LoadingEventType, content: string) => void,
  loadingStepData: LoadingStepData | null,
  setLoadingStepData: (value: React.SetStateAction<LoadingStepData>) => void,
  renderBotMessage: (text: string) => void,
  imageRef: React.RefObject<any[]>,
  setSidebarDisabled: (disabled: boolean) => void,
  question: string,
  clearLoading: () => void,
  addMessageToChat: (
    message: string | React.ReactNode | React.ReactNode[],
    sender: "user" | "bot",
    isChunk?: boolean,
    citations?: any[],
    generated_images_s3_keys?: any
  ) => void,
  setBotMessageCount: (value: React.SetStateAction<number>) => void,
  refreshChats: () => void,
  setChatHistory: (value: React.SetStateAction<any[]>) => void,
  setSocket: (value: React.SetStateAction<WebSocket | null>) => void,
  scrollToBottom: () => void,
  socket: WebSocket | null,
  chatHistory: any[],
  setChatLog: (value: React.SetStateAction<ChatLog[]>) => void,
  setShowDocxModal: (show: boolean) => void,
  setDocxContent: (content: string) => void,
  setExcelData: (data: any[][]) => void,
  setShowExcelModal: (show: boolean) => void,
  showAndSetModalImage: (src: string, alt: string) => void,
  setQuestion: (value: React.SetStateAction<string>) => void,
  chatLog: ChatLog[],
  setCurrentUserQuestion: (value: React.SetStateAction<string>) => void,
  conversationId: string,
  attachedFilesForMessage?: { id: string; name: string }[]
) => {
  const intermediateThoughtRef = React.useRef<string | null>(null);

  const connectWebSocket = async (userQuestion: string) => {
    citationsV2Ref.current = [];
    inlineBadgesRef.current = [];
    nextBadgeNumRef.current = 1;
    setError(null);

    let newSocket: WebSocket;
    const selectedFileIds = selectedFiles.map((file) => file.id);

    const enable_external_apis = [...enabledSources];

    if (selectedProject.id) {
      const projectBody: Record<string, unknown> = {
        question: userQuestion,
        project_name: selectedProject.name,
        project_id: selectedProject.id,
        session_id: chatSessionId,
        conversation_id: conversationIdRef.current,
        ...(selectedFileIds.length > 0 && { file_ids: selectedFileIds }),
        ...(enable_external_apis.length > 0 && {
          enable_external_apis,
        }),
        ...(attachedFilesForMessage?.length
          ? { attached_files: attachedFilesForMessage }
          : {}),
      };

      if (selectedReport.id) {
        const reportBody = {
          ...projectBody,
          report_name: selectedReport.name,
          report_id: selectedReport.id,
        };
        newSocket = new WebSocket(`${wsBaseUrl}/chat/report_chat`);
        newSocket.onopen = () => {
          newSocket.send(JSON.stringify(reportBody));
        };
      } else {
        newSocket = new WebSocket(`${wsBaseUrl}/chat/project_chat`);
        newSocket.onopen = () => {
          newSocket.send(JSON.stringify(projectBody));
        };
      }
    } else {
      newSocket = new WebSocket(`${wsBaseUrl}/chat/project_chat`);
      newSocket.onopen = () => {
        const body = {
          question: userQuestion,
          company_name: companyName,
          session_id: chatSessionId,
          conversation_id: conversationIdRef.current,
          ...(enable_external_apis.length > 0 && {
            enable_external_apis,
          }),
          ...(attachedFilesForMessage?.length
            ? { attached_files: attachedFilesForMessage }
            : {}),
        };

        newSocket.send(JSON.stringify(body));
      };
    }

    let accumulatedMessage = "";

    newSocket.onmessage = (event) => {
      const data = JSON.parse(event.data);

      if (data.type === "session_init") {
        setChatSessionId(data.session_id);
        chatSessionIdRef.current = data.session_id;
        return;
      }

      if (data.type === "conversation_id") {
        setConversationId(data.content);
        conversationIdRef.current = data.content;
        if (!conversationIdParam)
          setSearchParams({ conversationId: data.content });
        // refreshChats();
        return;
      }

      if (
        data.type === "step" ||
        data.type === "thinking" ||
        data.type === "reflecting" ||
        data.type === "code_generation_start" ||
        data.type === "code_execution_start" ||
        data.type === "generated_plots_processing"
      ) {
        setIsChatLoading(true);
        setShowLoading(true);

        if (data.type === "thinking" && !thinkingReceived.current) {
          thinkingReceived.current = true;
        }

        ingest(data.type, data.content || "");
        setLoadingStepData((prev) => {
          if (data.type === "step") {
            return { type: data.type, content: data.content || "" };
          }
          if (prev && prev.type === data.type) {
            return {
              type: data.type,
              content: (prev.content || "") + (data.content || ""),
            };
          }
          return { type: data.type, content: data.content || "" };
        });

        return;
      }

      if (data.type === "intermediate_steps") {
        setIsChatLoading(true);
        setShowLoading(true);

        // Add this new type to ingest and loadingStepData
        ingest(data.type, data.content || "");

        setLoadingStepData((prev) => ({
          type: data.type,
          content: (prev?.content || "") + (data.content || ""),
        }));

        intermediateThoughtRef.current = data.content || "";

        return;
      }

      if (data.type === "chunk") {
        const piece = data.content || "";
        accumulatedMessage += piece;
        renderBotMessage(accumulatedMessage);

        if (piece !== "") {
          if (!thinkingReceived.current) {
            setShowLoading(false);
          }
        }
        return;
      }

      if (data.type === "citation") {
        const mergedItems = normalizeNewCitationPacket(data.content);
        citationsV2Ref.current = mergeCitationItems(
          citationsV2Ref.current,
          mergedItems
        );

        const hoverLines: string[] = [];
        for (const it of mergedItems) {
          if (it.kind === "web") {
            const host = (() => {
              try {
                return new URL(it.url).hostname;
              } catch {
                return it.url;
              }
            })();
            const title = it.title || host;
            hoverLines.push(`${title} — ${host}`);
          } else if (it.kind === "document") {
            hoverLines.push(formatDocLabel(it));
          } else {
            hoverLines.push(formatTabularLabel(it));
          }
        }
        const hoverText = hoverLines.map((line) => `• ${line}`).join("\n");

        let primary: Partial<InlineBadge> = {};
        const first = mergedItems[0];

        if (first?.kind === "document") {
          const pageAnchor = first.overall
            ? first.overall[0]
            : first.page_ranges?.[0]?.[0] ?? first.pages?.[0];
          primary = {
            kind: "document",
            file_id: first.file_id,
            original_file: first.file_name,
            page_number: pageAnchor,
          };
        } else if (first?.kind === "tabular") {
          const [sheet, payload] =
            Object.entries(first.sheet_ranges || {})[0] || [];
          const rowAnchor = payload
            ? payload.overall
              ? payload.overall[0]
              : payload.ranges?.[0]?.[0]
            : undefined;
          primary = {
            kind: "tabular",
            file_id: first.file_id,
            original_file: first.file_name,
            sheet_name: sheet,
            start_row: rowAnchor,
          };
        } else if (first?.kind === "web") {
          primary = { kind: "web", url: first.url, title: first.title };
        }

        const n = nextBadgeNumRef.current++;
        inlineBadgesRef.current.push({
          n,
          project_id: selectedProject?.id ? String(selectedProject.id) : null,
          hoverText,
          ...primary,
        });

        accumulatedMessage += ` {[CIT:${n}]}`;
        renderBotMessage(accumulatedMessage);
        return;
      }

      // INLINE IMAGES: add IMG tokens right where plots arrive
      if (data.type === "plots_data") {
        const newImages: any[] = Array.isArray(data.content)
          ? data.content
          : [];

        const baseIndex = imageRef.current.length;
        for (let i = 0; i < newImages.length; i++) {
          imageRef.current.push(newImages[i]);
          accumulatedMessage += ` {[IMG:${baseIndex + i}]}`;
        }

        // Show the message with inline image tokens rendered
        renderBotMessage(accumulatedMessage);
        return;
      }

      if (data.type === "error") {
        setIsChatLoading(false);
        setSidebarDisabled(false);
        setError("Something unexpected occurred. Please try again.");
        console.error(data.content);
        setIsChatLoading(false);
        setLoadingStepData(null);
        clearLoading();
        setShowLoading(false);
        if (thinkingReceived.current) {
          thinkingReceived.current = false;
        }
        newSocket.close();
        return;
      }

      if (data.type === "end") {
        if (accumulatedMessage === "") return;

        setSidebarDisabled(false);

        const finalThought = intermediateThoughtRef.current || null;

        addMessageToChat(
          accumulatedMessage,
          "bot",
          true,
          undefined,
          imageRef.current
        );

        if (finalThought) {
          setChatLog((prev) => {
            const updated = [...prev];
            const lastBotIndex = updated
              .map((m) => m.sender)
              .lastIndexOf("bot");
            if (lastBotIndex !== -1) {
              updated[lastBotIndex] = {
                ...updated[lastBotIndex],
                // this field is what Assistant.tsx checks to show the panel
                intermediate_steps: finalThought,
              } as any;
            }
            return updated;
          });
        }

        const updatedChat = [...chatLog];
        const lastBotIndex = updatedChat
          .map((m) => m.sender)
          .lastIndexOf("bot");
        if (lastBotIndex !== -1) {
          updatedChat[lastBotIndex] = {
            ...updatedChat[lastBotIndex],
            message: accumulatedMessage,
          };
        }

        setChatHistory((prev) => [
          ...prev,
          {
            sender: "bot",
            message: accumulatedMessage,
            citations: citationsV2Ref.current,
            report_id: selectedReport.id ? `${selectedReport.id}` : null,
            project_id: selectedProject.id ? `${selectedProject.id}` : null,
            report_name: selectedReport?.name || null,
            project_name: selectedProject?.name || null,
            conversation_id: conversationIdRef.current,
            session_id: chatSessionIdRef.current,
            selected_files: selectedFiles,
            generated_images_s3_keys: imageRef.current,
            intermediate_steps:
              (loadingStepData?.type === "intermediate_steps"
                ? loadingStepData?.content
                : null) ?? null,
          },
        ]);

        if (accumulatedMessage !== "") setBotMessageCount((prev) => prev + 1);

        intermediateThoughtRef.current = null;
        setIsChatLoading(false);
        setLoadingStepData(null);
        clearLoading();
        setShowLoading(false);
        if (thinkingReceived.current) {
          thinkingReceived.current = false;
        }

        newSocket.close();
        refreshChats();
        return;
      }
    };

    newSocket.onerror = () => setError("Connection error occurred");

    newSocket.onclose = (event) => {
      const finalThought = intermediateThoughtRef.current || null;

      if (accumulatedMessage) renderBotMessage(accumulatedMessage);

      if (accumulatedMessage) {
        // make sure the visible last bot item gets the thought
        if (finalThought) {
          setChatLog((prev) => {
            const updated = [...prev];
            const lastBotIndex = updated
              .map((m) => m.sender)
              .lastIndexOf("bot");
            if (lastBotIndex !== -1) {
              updated[lastBotIndex] = {
                ...updated[lastBotIndex],
                intermediate_steps: finalThought,
              } as any;
            }
            return updated;
          });
        }

        setChatHistory((prev) => [
          ...prev,
          {
            sender: "bot",
            message: accumulatedMessage,
            citations: citationsV2Ref.current,
            report_id: selectedReport.id ? `${selectedReport.id}` : null,
            project_id: selectedProject.id ? `${selectedProject.id}` : null,
            report_name: selectedReport?.name || null,
            project_name: selectedProject?.name || null,
            conversation_id: conversationIdRef.current,
            session_id: chatSessionIdRef.current,
            selected_files: selectedFiles,
            generated_images_s3_keys: imageRef.current,
            intermediate_steps: finalThought, // ⬅️ persisted for refetch
          },
        ]);
        setBotMessageCount((prev) => prev + 1);
      }

      intermediateThoughtRef.current = null;
      setIsChatLoading(false);
      setSidebarDisabled(false);

      if (!event.wasClean) {
        setError("Connection was closed unexpectedly. Please try again.");
        console.error("WebSocket closed unexpectedly:", event);
      }

      setIsChatLoading(false);
      setLoadingStepData(null);
      clearLoading();
      setShowLoading(false);
    };

    setSocket(newSocket);
  };

  const sendQuestion = async (q: string, overrideHistory?: any[]) => {
    if (q.trim()) {
      setQuestion("");
      imageRef.current = []; // reset inline images for a fresh turn
      setIsChatLoading(true);
      setSidebarDisabled(true);
      setCurrentUserQuestion(q);
      addMessageToChat(q, "user");

      if (attachedFilesForMessage?.length) {
        setChatLog((prev) => {
          if (!prev.length) return prev;
          const next = [...prev];
          const last = next[next.length - 1] as any;
          last.attached_files = attachedFilesForMessage.map((f) => ({
            ...f,
            status: "processing", // show loader pill until they’re 'ready' later
          }));
          return next;
        });
      }

      const baseHistory = overrideHistory || chatHistory;
      const enable_external_apis = [...enabledSources];

      const updatedChatHistory = [
        ...baseHistory,
        {
          sender: "user",
          message: q,
          citations: [],
          report_id: selectedReport.id ? `${selectedReport.id}` : null,
          project_id: selectedProject.id ? `${selectedProject.id}` : null,
          report_name: selectedReport?.name || null,
          project_name: selectedProject?.name || null,
          conversation_id: conversationId,
          session_id: chatSessionIdRef.current,
          selected_files: selectedFiles,
          ...(enable_external_apis.length > 0 && {
            enable_external_apis,
          }),
          ...(attachedFilesForMessage?.length
            ? { attached_files: attachedFilesForMessage }
            : {}),
        },
      ];
      setChatHistory(updatedChatHistory);

      if (socket) socket.close();

      citationsV2Ref.current = [];
      connectWebSocket(q);
      scrollToBottom();
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    sendQuestion(question);
  };

  const tryAgain = () => {
    if (!chatHistory.length) return;
    const lastUserIndex = [...chatHistory]
      .map((m) => m.sender)
      .lastIndexOf("user");
    if (lastUserIndex === -1) return;
    const lastUserMessage = chatHistory[lastUserIndex];
    const updatedHistory = chatHistory.slice(0, lastUserIndex);
    setChatHistory(updatedHistory);
    setChatLog(
      renderMessagesForDisplay(updatedHistory, {
        setShowDocxModal,
        setDocxContent,
        setShowExcelModal,
        setExcelData,
        showAndSetModalImage,
      })
    );
    if (typeof lastUserMessage.message === "string")
      sendQuestion(lastUserMessage.message, updatedHistory);
  };

  const scopedDefined = chatLog.length > 0;

  return {
    sendQuestion,
    handleSubmit,
    tryAgain,
    scopedDefined,
  };
};
